import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Space,
  Descriptions,
  Tag,
  Spin,
  Alert,
  Row,
  Col,
  Statistic,
  Divider,
  Typography,
} from "antd";
import {
  PrinterOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BankOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import Header from "../../../../Componets/Header";
import ScrollWrapper from "../../../../Componets/ScrollWrapper";
import headers from "../../../../Data/Tables/Accounting/BankReconcilliation/headers";
import toast from "react-hot-toast";
import moment from "moment";
import ReactToPrint from "react-to-print";
import { export_reconciliation_report } from "../../../../Controllers/Accounting/BankReconcilliation";

const { Title, Text, Paragraph } = Typography;

const ReconciliationReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const componentRef = useRef();
  const [header, setHeader] = useState(headers);

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("user");
      const response = await export_reconciliation_report(id);
      if (response.success) {
        setReport(response.data);
      } else {
        toast.error(response.message || "Failed to fetch report");
      }
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    window.print();
  };

  const reconciledColumns = [
    {
      title: "S.No",
      key: "index",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => moment(date).format("DD/MM/YYYY"),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (amount) => amount?.toFixed(3),
    },
    {
      title: "Reconciled To",
      dataIndex: "reconciledTo",
      key: "reconciledTo",
      render: (journal) => journal?.referenceNumber || "-",
    },
    {
      title: "Reconciled Date",
      dataIndex: "reconciledDate",
      key: "reconciledDate",
      render: (date) => (date ? moment(date).format("DD/MM/YYYY") : "-"),
    },
  ];

  const unreconciledColumns = [
    {
      title: "S.No",
      key: "index",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => moment(date).format("DD/MM/YYYY"),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (amount) => amount?.toFixed(3),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6">
        <Alert message="Report not found" type="error" />
      </div>
    );
  }

  return (
    <Header headers={header} setHeaders={setHeader}>
      <ScrollWrapper>
          <div className="flex justify-between items-center mb-6 no-print">
            <div></div>
            <Space>
              <ReactToPrint
                trigger={() => (
                  <Button icon={<FilePdfOutlined />} type="primary">
                    Export PDF
                  </Button>
                )}
                content={() => componentRef.current}
              />
              <Button onClick={() => navigate("/app/bank-reconciliation")}>
                Back
              </Button>
            </Space>
          </div>
          <div ref={componentRef} className="print-report">
            <div className="text-center mb-8">
              <Title level={2}>Bank Reconciliation Report</Title>
              <Text type="secondary">
                Generated on{" "}
                {moment(report.generatedAt).format("DD/MM/YYYY HH:mm:ss")}
              </Text>
            </div>

            <Card className="mb-6">
              <Row gutter={16}>
                <Col span={12}>
                  <Descriptions
                    title="Bank Details"
                    bordered
                    size="small"
                    column={1}
                  >
                    <Descriptions.Item label="Bank Name">
                      {report.bankAccount?.name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Account Code">
                      {report.bankAccount?.code || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Statement ID">
                      {report.statementId}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col span={12}>
                  <Descriptions
                    title="Period Details"
                    bordered
                    size="small"
                    column={1}
                  >
                    <Descriptions.Item label="Period Start">
                      {moment(report.periodStart).format("DD/MM/YYYY")}
                    </Descriptions.Item>
                    <Descriptions.Item label="Period End">
                      {moment(report.periodEnd).format("DD/MM/YYYY")}
                    </Descriptions.Item>
                    <Descriptions.Item label="Statement Date">
                      {moment(report.statementDate).format("DD/MM/YYYY")}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            <Card className="mb-6">
              <Title level={4}>Balance Summary</Title>
              <Row gutter={16} className="mt-4">
                <Col span={8}>
                  <Statistic
                    title="Opening Balance"
                    value={report.openingBalance?.toFixed(3)}
                    prefix={<BankOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Closing Balance"
                    value={report.closingBalance?.toFixed(3)}
                    valueStyle={{
                      color: report.closingBalance >= 0 ? "#3f8600" : "#cf1322",
                    }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Net Change"
                    value={(
                      report.closingBalance - report.openingBalance
                    )?.toFixed(3)}
                  />
                </Col>
              </Row>
            </Card>

            <Card className="mb-6">
              <Title level={4}>Transaction Summary</Title>
              <Row gutter={16} className="mt-4">
                <Col span={6}>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {report.summary?.totalDebits?.toFixed(3) || "0.000"}
                    </div>
                    <div className="text-gray-500">Total Debits</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {report.summary?.totalCredits?.toFixed(3) || "0.000"}
                    </div>
                    <div className="text-gray-500">Total Credits</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {report.summary?.reconciledCount || 0}
                    </div>
                    <div className="text-gray-500">Reconciled Transactions</div>
                    <div className="text-sm text-gray-400">
                      Amount:{" "}
                      {report.summary?.reconciledAmount?.toFixed(3) || "0.000"}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {report.summary?.unreconciledCount || 0}
                    </div>
                    <div className="text-gray-500">
                      Unreconciled Transactions
                    </div>
                    <div className="text-sm text-gray-400">
                      Amount:{" "}
                      {report.summary?.unreconciledAmount?.toFixed(3) ||
                        "0.000"}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            <Card className="mb-6">
              <Title level={4}>Reconciliation Status</Title>
              <div className="mt-4">
                <Row gutter={16}>
                  <Col span={12}>
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">
                          Reconciliation Status:
                        </span>
                        <Tag
                          color={
                            report.summary?.unreconciledCount === 0
                              ? "success"
                              : "warning"
                          }
                          className="text-lg px-3 py-1"
                        >
                          {report.summary?.unreconciledCount === 0
                            ? "Fully Reconciled"
                            : "Partially Reconciled"}
                        </Tag>
                      </div>
                      <Divider className="my-3" />
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">
                          Reconciliation Rate:
                        </span>
                        <span className="font-semibold">
                          {(
                            (report.summary?.reconciledCount /
                              (report.summary?.reconciledCount +
                                report.summary?.unreconciledCount)) *
                              100 || 0
                          ).toFixed(1)}
                          %
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">
                          Bank Balance vs Book Balance:
                        </span>
                        <span
                          className={`font-semibold ${
                            Math.abs(
                              report.closingBalance -
                                (report.openingBalance +
                                  report.summary?.totalCredits -
                                  report.summary?.totalDebits),
                            ) < 0.01
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {Math.abs(
                            report.closingBalance -
                              (report.openingBalance +
                                report.summary?.totalCredits -
                                report.summary?.totalDebits),
                          ).toFixed(3)}
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>
            {report.reconciledTransactions?.length > 0 && (
              <Card className="mb-6">
                <Title level={4}>
                  <CheckCircleOutlined className="mr-2 text-green-500" />
                  Reconciled Transactions (
                  {report.reconciledTransactions.length})
                </Title>
                <Table
                  columns={reconciledColumns}
                  dataSource={report.reconciledTransactions}
                  rowKey={(record, index) => index}
                  size="small"
                  pagination={{ pageSize: 10 }}
                  className="mt-4"
                />
              </Card>
            )}

            {report.unreconciledTransactions?.length > 0 && (
              <Card className="mb-6">
                <Title level={4}>
                  <CloseCircleOutlined className="mr-2 text-orange-500" />
                  Unreconciled Transactions (
                  {report.unreconciledTransactions.length})
                </Title>
                <Table
                  columns={unreconciledColumns}
                  dataSource={report.unreconciledTransactions}
                  rowKey={(record, index) => index}
                  size="small"
                  pagination={{ pageSize: 10 }}
                  className="mt-4"
                />
              </Card>
            )}

            <div className="text-center text-gray-400 text-sm mt-8 pt-4 border-t">
              <p>
                This is a system-generated report. For any discrepancies, please
                contact your system administrator.
              </p>
              <p>
                Generated by: {report.generatedBy?.name || "System"} |{" "}
                {moment(report.generatedAt).format("DD/MM/YYYY HH:mm:ss")}
              </p>
            </div>
          </div>

          <style jsx global>{`
            @media print {
              .no-print {
                display: none !important;
              }
              .print-report {
                padding: 20px;
              }
              .ant-card {
                break-inside: avoid;
                page-break-inside: avoid;
              }
              .ant-table {
                font-size: 10pt;
              }
              .ant-table-cell {
                padding: 4px 8px !important;
              }
              @page {
                size: A4;
                margin: 1.5cm;
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
          `}</style>
      </ScrollWrapper>
    </Header>
  );
};

export default ReconciliationReport;
